import { connect } from 'react-redux';
import { AppState } from '../../app-state';
import { setAnnotationPanePageOffsets } from '../../layout/actions/ui-panes.actions';
import { HighlightCollection } from '../../text-highlighter/models/highlight-collection.model';
import { TextSelection } from '../../text-highlighter/models/text-selection';
import {
  clearAnnotationSelection,
  setAnnotationHighlights,
  setAnnotationSelection,
  setFocusedAnnotationId,
} from '../actions/ui-annotations.actions';
import {
  addToChainNesting,
  setSelectedAnnotations,
  toggleAnnotationSelection,
} from '../actions/annotations.actions';
import { createAnnotationRelation } from '../actions/annotation-relations.actions';
import { AnnotationViewer as AnnotationViewerComponent } from '../components/annotation-viewer.component';
import {
  selectAnnotationPagesArray,
  selectCurrentAnnotationPageNumber,
  selectFullText,
} from '../selectors/annotation-pages.selectors';
import { selectAnnotationsForHighlights } from '../selectors/annotation.selectors';
import {
  selectAnnotationHighlightColor,
  selectEmphasizeAnnotationId,
} from '../selectors/ui.selectors';
import { selectActiveAnnotationSchema } from '../selectors/annotation-schema.selectors';
import { AnnotationSchema } from '../models/annotation-schema.model';
import { selectSourceDocumentIsEditable } from '../../documents/selectors/source-document.selectors';
import { RELATION_TYPES } from '../../lib/relations/relation-types';
import { handleAnnotationClick } from '../utils/annotation-selection';

const mapStateToProps = (state: AppState) => ({
  activeSchema:
    selectActiveAnnotationSchema(state) || AnnotationSchema.unknownSchema(),
  page: selectCurrentAnnotationPageNumber(state),
  pages: selectAnnotationPagesArray(state),
  text: selectFullText(state),
  annotations: selectAnnotationsForHighlights(state),
  selectionColor: selectAnnotationHighlightColor(state),
  emphasizeAnnotationId: selectEmphasizeAnnotationId(state),
  selectedAnnotations: state.annotations.selectedAnnotations,
  chainNestingOrder: state.annotations.chainNestingOrder,
  editable: selectSourceDocumentIsEditable(state),
});

const mapDispatchToProps = (dispatch: any) => ({
  onSelectionChange: (sel: TextSelection) => {
    if (sel.isSelected) {
      dispatch(
        setAnnotationSelection(
          [{ start: sel.startOffset, end: sel.endOffset, text: sel.text }],
          'text-viewer',
        ),
      );
    }
  },
  onSelectionCleared: () => dispatch(clearAnnotationSelection()),
  updatePageOffsets: (offsets: number[]) =>
    dispatch(setAnnotationPanePageOffsets(offsets)),
  onHighlightsUpdated: (highlights: HighlightCollection) =>
    dispatch(setAnnotationHighlights(highlights)),
  dispatchSetSelectedAnnotations: (ids: string[]) =>
    dispatch(setSelectedAnnotations(ids)),
  dispatchFocusAnnotation: (id: string) => dispatch(setFocusedAnnotationId(id)),
  dispatchAddToChainNesting: (id: string) => dispatch(addToChainNesting(id)),
  dispatchToggleAnnotationSelection: (id: string) =>
    dispatch(toggleAnnotationSelection(id)),
  dispatchCreateRelations: (pairs: { left: string; right: string }[]) => {
    dispatch(
      createAnnotationRelation(
        pairs.map(p => ({ ...p, relationType: RELATION_TYPES.CHILD_OF })),
      ),
    );
  },
});

const mergeProps = (stateProps: any, dispatchProps: any, ownProps: any) => {
  const {
    dispatchSetSelectedAnnotations,
    dispatchAddToChainNesting,
    dispatchToggleAnnotationSelection,
    dispatchCreateRelations,
    dispatchFocusAnnotation,
    ...restDispatch
  } = dispatchProps;

  const handlers = {
    setSelectedAnnotations: dispatchSetSelectedAnnotations,
    toggleAnnotationSelection: dispatchToggleAnnotationSelection,
    addToChainNesting: dispatchAddToChainNesting,
    createRelations: dispatchCreateRelations,
    focusAnnotation: dispatchFocusAnnotation,
  };

  return {
    ...ownProps,
    ...stateProps,
    ...restDispatch,
    onClearAnnotationSelection: () => {
      if (stateProps.selectedAnnotations.length > 0) {
        dispatchSetSelectedAnnotations([]);
      }
    },
    onAnnotationClick: (annotationId: string, e: React.MouseEvent) =>
      handleAnnotationClick(
        annotationId,
        e,
        stateProps.selectedAnnotations,
        stateProps.chainNestingOrder.length > 0,
        handlers,
      ),
  };
};

export const AnnotationViewer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(AnnotationViewerComponent);
